import { ReceivingDetailInterface as CoreReceiving } from '../..';
import { CoreGridOutput, GridInput } from '../grid';
import { DecimalValue } from '../decimal';
import { FileCreatorRole } from '../../types/receivings/FileCreatorRole';

/** GWMS receiving attachment (maps to Core FILES / receivings_files). */
export interface ReceivingFileInput {
    id?: number;
    type: string;
    data?: string;
    fileName: string;
    fileType: string;
    fileSize?: string;
    description?: string | null;
    /** Ignored on write from MyS; Core sets from JWT. GWMS clients may send for tests. */
    creatorRole?: FileCreatorRole | null;
    /** Upload timestamp from `files.CREATED_AT` (response only). */
    createdAt?: Date | string | null;
}

export interface Receiving {
    /**
     * Internal numeric ID of the receiving in Core/GWMS.
     * Exposed so API clients can uniquely reference a receiving returned from the API.
     */
    id?: number;
    /** Advanced Shipping Notice identifier */
    asn?: string;
    eshopReceivingId: string;
    description: string | null;
    trackingNumber: string | null;
    supplier: string | null;
    estimatedDelivery: string;
    createdAt?: string;
    slaDate?: string | null;
    asnLabelEmail?: string | null;
    deliveryNoteNumber: string | null;
    deliveryType: string | null;
    urgent: string | null;
    duty?: boolean;
    unitsAmount: number;
    driverCompany: string | null;
    driverTrailerLicensePlate: string | null;
    driverName: string | null;
    driverPhone: string | null;
    driverDispatcherContact: string | null;
    status: string | null;
    products: ReceivingProduct[];
    eshopNote?: string | null;
    priority?: number;
    containerNumber?: string | null;
    announcedPalletsCount?: number | null;
    receivingType?: string | null;
    realDelivery?: Date | string | null;
    slaSuspended?: string | null;
    photoDocumentationTaken?: string | null;
    deliveredPalletsCount?: number | null;
    deliveredCartonsCount?: number | null;
    incoterms?: string | null;
    returnedEurPalletsCount?: number | null;
    deliveredStandardPalletsCount?: number | null;
    returnedStandardPalletsCount?: number | null;
    deliveredOtherPalletsCount?: number | null;
    deliveredBrokenPalletsCount?: number | null;
    skladonNote?: string | null;
    files?: ReceivingFileInput[];
    // Additional fields from Core
    quantity?: number | null;
    received?: number | null;
    diffQuantity?: number | null;
    driverCompanyOtherName?: string | null;
    carrierInfo?: string | null;
    handlingUnits?: string | null;
    unloadedAt?: Date | string | null;
    receivedAt?: Date | string | null;
    confirmedAt?: Date | string | null;
    canceledAt?: Date | string | null;
}

export interface ReceivingProduct {
    sku: string;
    quantity: number;
    received: number | null;
    lot: string | null;
    expirationDate: Date | string | null;
    productPrice?: DecimalValue | null;
    lineState: string;
}

/**
 * Grid rows come from Core's `receiving_products_sum_view`, which adds the aggregated
 * product quantities on top of the plain receiving columns.
 */
export type CoreReceivingGridRow = Omit<CoreReceiving, 'PRODUCTS' | 'PROVIDER'> & {
    QUANTITY?: number | null;
    RECEIVED?: number | null;
    DIFF_QUANTITY?: number | null;
};

export type CoreReceivingsGridOutput = CoreGridOutput<CoreReceivingGridRow>;
// Grid output interface for GWMS
export interface ReceivingsGridOutput {
    data: Receiving[];
    request: GridInput;
    totalCount: number;
}

export const fromCoreReceivingNames = {
    ID: 'id',
    ASN: 'asn',
    CREATED_AT: 'createdAt',
    SLA_DATE: 'slaDate',
    QUANTITY: 'quantity',
    RECEIVED: 'received',
    DIFF_QUANTITY: 'diffQuantity',
    DRIVER_COMPANY_OTHER_NAME: 'driverCompanyOtherName',
    CONFIRMED_AT: 'confirmedAt',
    UNLOADED_AT: 'unloadedAt',
    RECEIVED_AT: 'receivedAt',
    ESHOP_RECEIVING_ID: 'eshopReceivingId',
    DELIVERY_NOTE_NUMBER: 'deliveryNoteNumber',
    DELIVERY_TYPE: 'deliveryType',
    DESCRIPTION: 'description',
    DRIVER_COMPANY: 'driverCompany',
    DRIVER_DISPATCHER_CONTACT: 'driverDispatcherContact',
    DRIVER_NAME: 'driverName',
    DRIVER_PHONE: 'driverPhone',
    DRIVER_TRAILER_LICENSE_PLATE: 'driverTrailerLicensePlate',
    DUTY: 'duty',
    ESTIMATED_DELIVERY: 'estimatedDelivery',
    STATUS: 'status',
    SUPPLIER: 'supplier',
    TRACKING_NUMBER: 'trackingNumber',
    UNITS_AMOUNT: 'unitsAmount',
    URGENT: 'urgent',
    ESHOP_NOTE: 'eshopNote',
    PRIORITY: 'priority',
    CONTAINER_NUMBER: 'containerNumber',
    ANNOUNCED_PALLETS_COUNT: 'announcedPalletsCount',
    RECEIVING_TYPE: 'receivingType',
    REAL_DELIVERY: 'realDelivery',
    SLA_SUSPENDED: 'slaSuspended',
    PHOTO_DOCUMENTATION_TAKEN: 'photoDocumentationTaken',
    DELIVERED_PALLETS_COUNT: 'deliveredPalletsCount',
    DELIVERED_CARTONS_COUNT: 'deliveredCartonsCount',
    CANCELED_AT: 'canceledAt',

    INCOTERMS: 'incoterms',
    RETURNED_EUR_PALLETS_COUNT: 'returnedEurPalletsCount',
    DELIVERED_STANDARD_PALLETS_COUNT: 'deliveredStandardPalletsCount',
    RETURNED_STANDARD_PALLETS_COUNT: 'returnedStandardPalletsCount',
    DELIVERED_OTHER_PALLETS_COUNT: 'deliveredOtherPalletsCount',
    DELIVERED_BROKEN_PALLETS_COUNT: 'deliveredBrokenPalletsCount',
    SKLADON_NOTE: 'skladonNote',
    ASN_LABEL_EMAIL: 'asnLabelEmail',
};

export const toCoreReceivingNames = {
    id: 'ID',
    asn: 'ASN',
    createdAt: 'CREATED_AT',
    slaDate: 'SLA_DATE',
    quantity: 'QUANTITY',
    received: 'RECEIVED',
    diffQuantity: 'DIFF_QUANTITY',
    driverCompanyOtherName: 'DRIVER_COMPANY_OTHER_NAME',
    canceledAt: 'CANCELED_AT',
    confirmedAt: 'CONFIRMED_AT',
    unloadedAt: 'UNLOADED_AT',
    receivedAt: 'RECEIVED_AT',
    eshopReceivingId: 'ESHOP_RECEIVING_ID',
    deliveryNoteNumber: 'DELIVERY_NOTE_NUMBER',
    deliveryType: 'DELIVERY_TYPE',
    description: 'DESCRIPTION',
    driverCompany: 'DRIVER_COMPANY',
    driverDispatcherContact: 'DRIVER_DISPATCHER_CONTACT',
    driverName: 'DRIVER_NAME',
    driverPhone: 'DRIVER_PHONE',
    driverTrailerLicensePlate: 'DRIVER_TRAILER_LICENSE_PLATE',
    duty: 'DUTY',
    estimatedDelivery: 'ESTIMATED_DELIVERY',
    status: 'STATUS',
    supplier: 'SUPPLIER',
    trackingNumber: 'TRACKING_NUMBER',
    unitsAmount: 'UNITS_AMOUNT',
    urgent: 'URGENT',
    eshopNote: 'ESHOP_NOTE',
    priority: 'PRIORITY',
    containerNumber: 'CONTAINER_NUMBER',
    announcedPalletsCount: 'ANNOUNCED_PALLETS_COUNT',
    receivingType: 'RECEIVING_TYPE',
    realDelivery: 'REAL_DELIVERY',
    slaSuspended: 'SLA_SUSPENDED',
    photoDocumentationTaken: 'PHOTO_DOCUMENTATION_TAKEN',
    deliveredPalletsCount: 'DELIVERED_PALLETS_COUNT',
    deliveredCartonsCount: 'DELIVERED_CARTONS_COUNT',
    incoterms: 'INCOTERMS',
    returnedEurPalletsCount: 'RETURNED_EUR_PALLETS_COUNT',
    deliveredStandardPalletsCount: 'DELIVERED_STANDARD_PALLETS_COUNT',
    returnedStandardPalletsCount: 'RETURNED_STANDARD_PALLETS_COUNT',
    deliveredOtherPalletsCount: 'DELIVERED_OTHER_PALLETS_COUNT',
    deliveredBrokenPalletsCount: 'DELIVERED_BROKEN_PALLETS_COUNT',
    skladonNote: 'SKLADON_NOTE',
    asnLabelEmail: 'ASN_LABEL_EMAIL',
};
